import DataGrid, { DataGridConfig } from 'components/DataGrid';
import IconButton from 'components/IconButton';
import NoRecordFound from 'components/NoRecordFound/NoRecordFound';
import history from 'history.js';
import { isAdminUserLoggedIn } from 'lib/helper';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE } from 'views/types';

import BPAControlIgnoreModal from './BPAControlIgnoreModal';
import { BPATopBenchmarkItem, BPATopControlItem } from '../../types';

interface DetailGridProps {
  rows: unknown[];
  dataGridConfig: Pick<DataGridConfig, 'rowIdKey' | 'columns'>;
}

/**
 * The same client-paginated DataGrid layout used by PCM View Details dialogs.
 * Supplying the complete filtered ranking keeps pagination local and avoids a
 * second dashboard API request when the user opens the dialog.
 */
const DetailGrid = ({ rows, dataGridConfig }: DetailGridProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => setCurrentPage(0), [rows]);
  const paginatedRows = useMemo(
    () => rows.slice(currentPage * pageSize, (currentPage + 1) * pageSize),
    [currentPage, pageSize, rows],
  );
  const config = useMemo<DataGridConfig>(
    () => ({
      ...dataGridConfig,
      cssClassName: 'bpa-top-findings-grid',
      paginationConfig: {
        total_count: rows.length,
        pageNumber: currentPage,
        pageSize,
        onPageChange: setCurrentPage,
        onPageSizeChange: (newPageSize) => {
          setPageSize(newPageSize);
          setCurrentPage(0);
        },
      },
    }),
    [currentPage, dataGridConfig, pageSize, rows.length],
  );

  if (rows.length === 0) return <NoRecordFound />;
  return <DataGrid config={config} data={paginatedRows} />;
};

const ViewFindingsButton = ({ id, url }: { id: string; url: string }) => (
  <IconButton
    id={id}
    iconClass="fa fa-bug"
    size="sm"
    tooltip="View Findings"
    onClick={() => history.push(url)}
  />
);

const ControlActions = ({
  row,
  url,
  providerCodes,
  integrationIds,
  onIgnoreComplete,
}: {
  row: BPATopControlItem;
  url: string;
  providerCodes: string[];
  integrationIds: number[];
  onIgnoreComplete: () => void;
}) => {
  const [isIgnoreModalOpen, setIsIgnoreModalOpen] = useState(false);

  return (
    <>
      <div
        className="d-flex align-items-center justify-content-end"
        style={{ gap: '0.25rem' }}
      >
        <ViewFindingsButton
          id={`bpa_control_findings_${row.control_pk}`}
          url={url}
        />
        {isAdminUserLoggedIn() && (
          <IconButton
            id={`bpa_control_ignore_${row.control_pk}`}
            iconElement={
              <i className="fa fa-ban text-danger" aria-hidden="true" />
            }
            size="sm"
            tooltip="Ignore active findings for this control"
            onClick={() => setIsIgnoreModalOpen(true)}
          />
        )}
      </div>
      {isIgnoreModalOpen && (
        <BPAControlIgnoreModal
          control={row}
          providerCodes={providerCodes}
          integrationIds={integrationIds}
          onClose={() => setIsIgnoreModalOpen(false)}
          onComplete={onIgnoreComplete}
        />
      )}
    </>
  );
};

export const ControlFindingsDetails = ({
  rows,
  getDrillDownUrl,
  providerCodes,
  integrationIds,
  onIgnoreComplete,
}: {
  rows: BPATopControlItem[];
  getDrillDownUrl: (row: BPATopControlItem) => string;
  providerCodes: string[];
  integrationIds: number[];
  onIgnoreComplete: () => void;
}) => (
  <DetailGrid
    rows={rows}
    dataGridConfig={{
      rowIdKey: 'control_pk',
      columns: [
        { key: 'provider', label: 'Provider' },
        {
          key: 'control_pk',
          label: 'Control ID',
          enableCopyToClipboard: true,
          overrideContents: (row: BPATopControlItem) => (
            <Link to={getDrillDownUrl(row)}>{row.control_pk}</Link>
          ),
        },
        {
          key: 'description',
          label: 'Name',
          enableCopyToClipboard: true,
          width: '100%',
          maxCharLength: 75,
          overrideContents: (row: BPATopControlItem) => (
            <Link to={getDrillDownUrl(row)}>{row.description}</Link>
          ),
        },
        {
          key: 'benchmark_description',
          label: 'Benchmark',
          enableCopyToClipboard: true,
          width: '100%',
          maxCharLength: 75,
        },
        { key: 'severity_label', label: 'Severity' },
        { key: 'active_count', label: 'Count' },
        {
          key: 'action',
          label: 'Action',
          width: '110px',
          overrideContents: (row: BPATopControlItem) => (
            <ControlActions
              row={row}
              url={getDrillDownUrl(row)}
              providerCodes={providerCodes}
              integrationIds={integrationIds}
              onIgnoreComplete={onIgnoreComplete}
            />
          ),
        },
      ],
    }}
  />
);

export const BenchmarkFindingsDetails = ({
  rows,
  getDrillDownUrl,
}: {
  rows: BPATopBenchmarkItem[];
  getDrillDownUrl: (row: BPATopBenchmarkItem) => string;
}) => (
  <DetailGrid
    rows={rows}
    dataGridConfig={{
      rowIdKey: 'benchmark_id',
      columns: [
        { key: 'provider', label: 'Provider' },
        {
          key: 'description',
          label: 'Benchmark',
          enableCopyToClipboard: true,
          width: '100%',
          maxCharLength: 90,
          overrideContents: (row: BPATopBenchmarkItem) => (
            <Link to={getDrillDownUrl(row)}>{row.description}</Link>
          ),
        },
        { key: 'active_count', label: 'Count' },
        {
          key: 'action',
          label: 'Action',
          width: '80px',
          overrideContents: (row: BPATopBenchmarkItem) => (
            <ViewFindingsButton
              id={`bpa_benchmark_findings_${row.benchmark_id}`}
              url={getDrillDownUrl(row)}
            />
          ),
        },
      ],
    }}
  />
);
